/* =========================================================================
 * Oasis identity + engagement tracking module — added 21 Aug 2026
 * -------------------------------------------------------------------------
 * Rides alongside assets/klaviyo-tracking.js (window.OasisTrack). Adds:
 *   1. A session-ping beacon to the Oasis lead warehouse. The server reads
 *      the caller's IP from the request itself; nothing here ever sees it.
 *      One ping per session, another when the visitor identifies.
 *   2. Browser timezone written to the Klaviyo profile once identified.
 *   3. Started Reservation coverage for ALL reserve paths (inline <head>
 *      capture queue, quiz reserve button, /cart/add permalink links).
 *   4. New engagement events: Viewed Fixed Plan, Browsed Gallery,
 *      Opened FAQ Question.
 *   5. Real-time warehouse dual-write for those events when identified
 *      (Klaviyo relay gaps are backstopped by the 15-min poll regardless).
 *
 * No PII is read beyond what klaviyo-tracking.js already persists
 * (oasis_known_email / oasis_known_kx). Consent: events route through
 * OasisTrack, which is consent-gated; the session ping carries no marketing
 * payload (first-party analytics).
 * ========================================================================= */
(function () {
  'use strict';
  if (window.OasisIdentityTrack && window.OasisIdentityTrack.__loaded) return;

  var PING_URL = 'https://hwubbmgzhfrewychjboj.supabase.co/functions/v1/session-ping';
  var LEAD_EVENT_URL = 'https://hwubbmgzhfrewychjboj.supabase.co/functions/v1/lead-event';

  // ----- tiny helpers -----------------------------------------------------
  function ls(k) { try { return localStorage.getItem(k); } catch (e) { return null; } }
  function anonId() {
    var v = ls('oasis_anonymous_id');
    if (v) return v;
    v = 'anon-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
    try { localStorage.setItem('oasis_anonymous_id', v); } catch (e) {}
    return v;
  }
  // Visit number: how many separate visits this browser has made. Counted
  // once per session, so a ten-page visit is still one visit. First visit is
  // 1. Lets a card say "back for the fourth time" without reconstructing it
  // from session history after the fact.
  function visitNumber() {
    try {
      var n = parseInt(localStorage.getItem('oasis_visit_count') || '0', 10) || 0;
      if (!sessionStorage.getItem('oasis_visit_counted')) {
        n += 1;
        localStorage.setItem('oasis_visit_count', String(n));
        sessionStorage.setItem('oasis_visit_counted', '1');
        if (!localStorage.getItem('oasis_first_visit_at')) {
          localStorage.setItem('oasis_first_visit_at', new Date().toISOString());
        }
      }
      return n || 1;
    } catch (e) { return 1; }
  }
  function firstVisitAt() { return ls('oasis_first_visit_at'); }

  function knownEmail() { return ls('oasis_known_email'); }
  function knownKx() { return ls('oasis_known_kx'); }
  function browserTz() {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || null; } catch (e) { return null; }
  }
  function sessionId() {
    try {
      var s = sessionStorage.getItem('oasis_idt_session');
      if (!s) {
        s = 'ss-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
        sessionStorage.setItem('oasis_idt_session', s);
      }
      return s;
    } catch (e) { return 'ss-volatile'; }
  }
  function pageUtm() {
    try {
      var p = new URLSearchParams(location.search), out = {}, any = false;
      ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'fbclid'].forEach(function (k) {
        var v = p.get(k);
        if (v) { out[k] = String(v).slice(0, 180); any = true; }
      });
      return any ? out : null;
    } catch (e) { return null; }
  }
  function post(url, payload) {
    try {
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
        credentials: 'omit',
        mode: 'cors'
      }).catch(function () {});
    } catch (e) {}
  }

  // ----- 1. session ping --------------------------------------------------
  var pinged = {};
  function ping(reason) {
    var key = reason === 'identify' ? 'identify' : 'load';
    if (pinged[key]) return;
    pinged[key] = 1;
    post(PING_URL, {
      anon_id: anonId(),
      session_id: sessionId(),
      email: knownEmail() || undefined,
      kx: knownKx() || undefined,
      tz: browserTz() || undefined,
      visit_number: visitNumber(),
      first_visit_at: firstVisitAt() || undefined,
      path: (location.pathname || '/').slice(0, 300),
      utm: pageUtm() || undefined,
      reason: reason
    });
  }

  // ----- warehouse dual-write (identified only) ---------------------------
  function warehouse(eventName, props) {
    var em = knownEmail();
    if (!em) return;
    var p = {};
    for (var k in (props || {})) { if (k !== '__unique_id') p[k] = props[k]; }
    p.unique_id = 'thm:' + eventName + ':' + sessionId() + ':' + (p.lot_handle || location.pathname);
    post(LEAD_EVENT_URL, {
      source: 'theme',
      event_name: eventName,
      email: em,
      properties: p,
      occurred_at: new Date().toISOString()
    });
  }

  function track(name, props) {
    var p = props || {};
    try { if (p.visit_number === undefined) p.visit_number = visitNumber(); } catch (e) {}
    try { if (window.OasisTrack && window.OasisTrack.track) window.OasisTrack.track(name, p); } catch (e) {}
    warehouse(name, p);
  }

  // ----- 2. identify moment: second ping + timezone property --------------
  function onIdentified() {
    ping('identify');
    try {
      var vn = visitNumber();
      if (window.OasisTrack && window.OasisTrack.setProperty) {
        window.OasisTrack.setProperty('Visit Number', vn);
        var fv = firstVisitAt();
        if (fv) window.OasisTrack.setProperty('First Visit At', fv);
      }
      var tz = browserTz();
      if (tz && window.OasisTrack && window.OasisTrack.setProperty) {
        window.OasisTrack.setProperty('Browser Timezone', tz);
      }
    } catch (e) {}
  }

  // ----- 3. Started Reservation, every path -------------------------------
  function currentHandle() {
    var ctx = window.OasisTrackContext || {};
    return (ctx.product && ctx.product.handle) || '';
  }
  function readMonthly() {
    var val = NaN;
    var hidden = document.querySelector('[data-oasis-property="monthly-payment"]');
    if (hidden) val = parseFloat(String(hidden.value || hidden.textContent || '').replace(/[^0-9.]/g, ''));
    if (!isFinite(val) || val <= 0) {
      var el = document.getElementById('monthlyPaymentResult');
      if (el) val = parseFloat(String(el.value || el.textContent || '').replace(/[^0-9.]/g, ''));
    }
    if (!isFinite(val) || val <= 0) {
      var fp = document.querySelector('.oasis-pp__fig--monthly .oasis-pp__fig-val');
      if (fp) val = parseFloat(String(fp.textContent || '').replace(/[^0-9.]/g, ''));
    }
    return (isFinite(val) && val > 0) ? Math.round(val * 100) / 100 : undefined;
  }
  function fireReserve(handle, source) {
    var h = handle || currentHandle();
    track('Started Reservation', {
      __unique_id: sessionId() + ':resv:' + (h || 'unknown'),
      lot_handle: h || undefined,
      source: source || 'form',
      monthly_payment: readMonthly()
    });
  }
  function drainReserveQueue() {
    var q = window.__oasisReserveQueue;
    if (q && q.length) {
      q.splice(0).forEach(function (it) { fireReserve(it && it.handle, (it && it.source) || 'form'); });
    }
  }
  window.addEventListener('oasis:reserve-intent', function (ev) {
    var d = (ev && ev.detail) || {};
    fireReserve(d.handle, d.source || 'form');
  });
  // Quiz reserve button + landing-page /cart/add permalinks.
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    var t = e.target.closest('#lot-reserve-btn, a[href*="/cart/add"]');
    if (!t) return;
    var handle = t.getAttribute('data-product-handle') || t.getAttribute('data-lot') || '';
    fireReserve(handle, t.id === 'lot-reserve-btn' ? 'quiz' : 'permalink');
  }, true);

  // ----- 4a. Viewed Fixed Plan --------------------------------------------
  function wireFixedPlan() {
    var el = document.querySelector('.oasis-pp');
    if (!el || !('IntersectionObserver' in window)) return;
    var handle = currentHandle();
    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (!entries[i].isIntersecting) continue;
        io.disconnect();
        track('Viewed Fixed Plan', {
          __unique_id: sessionId() + ':fp:' + (handle || location.pathname),
          lot_handle: handle || undefined,
          monthly_payment: readMonthly()
        });
        break;
      }
    }, { threshold: 0.4 });
    io.observe(el);
  }

  // ----- 4b. Browsed Gallery ----------------------------------------------
  var galleryClicks = 0, galleryFired = false;
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    var t = e.target.closest('.gallery-carousel, .gallery-thumbs, .gallery-nav, .gallery-arrow, [data-slide-index]');
    if (!t) return;
    galleryClicks++;
    if (galleryClicks >= 3 && !galleryFired) {
      galleryFired = true;
      var handle = currentHandle();
      track('Browsed Gallery', {
        __unique_id: sessionId() + ':gal:' + (handle || location.pathname),
        lot_handle: handle || undefined,
        interactions: galleryClicks
      });
    }
  }, true);

  // ----- 4c. Opened FAQ Question ------------------------------------------
  var faqFired = 0;
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    var s = e.target.closest(
      'details summary, summary.faq-summary, .faq-question, [data-faq-question], ' +
      '.accordion__header, .accordion-header'
    );
    if (!s) return;
    if (s.closest('.oasis-pp')) return;          // the fixed plan's own expander
    if (s.closest('.plans-disclosure')) return;  // concept plans has its own event
    // Navigation drawers are not questions.
    if (s.closest('header, nav, .menu-drawer, .header, .header-wrapper')) return;

    // 27 Aug 2026: the old guard required the URL to contain faq/question/
    // knowledge, or an ancestor matching .faq/.faqs/[data-faq]/#faq. Lot
    // pages are /products/... and their accordions are <details
    // class="faq-details">, which matches none of that, so the thirteen
    // questions on every lot page had never once fired. Match on the
    // element's own faq/accordion naming as well as its container's.
    var self = (typeof s.className === 'string' ? s.className : '') + ' ' + (s.id || '');
    var box = s.closest('[class*="faq" i], [id*="faq" i], [data-faq], .accordion, [class*="accordion" i]');
    var onFaqPage = /faq|question|knowledge/i.test(location.pathname);
    if (!onFaqPage && !box && !/faq/i.test(self)) return;

    // Only count opening a question, never closing one.
    var host = s.parentElement;
    if (host && host.tagName === 'DETAILS' && host.open) return;

    if (faqFired >= 5) return;
    faqFired++;
    var q = (s.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 90);
    track('Opened FAQ Question', {
      __unique_id: sessionId() + ':faq:' + q.slice(0, 40),
      question: q || undefined,
      lot_handle: currentHandle() || undefined,
      path: location.pathname.slice(0, 200)
    });
  }, true);

  // ----- boot -------------------------------------------------------------
  function boot() {
    drainReserveQueue();
    wireFixedPlan();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  ping('load');
  if (knownEmail() || knownKx()) {
    onIdentified();
  } else {
    // Identity can arrive later in the page's life (form submit, kx link).
       // No cutoff. Identity can arrive at any point in a long visit: somebody
    // who reads a lot page for twenty minutes and then submits the reservation
    // form must still be seen. Tight for the first two minutes, then a slow
    // heartbeat for as long as the page stays open. Stops for good on the
    // first hit, so it can never fire onIdentified twice.
    (function pollForIdentity() {
      var started = Date.now();
      function tick() {
        try {
          if (knownEmail() || knownKx()) { onIdentified(); return; }
        } catch (e) {}
        setTimeout(tick, Date.now() - started < 120000 ? 4000 : 20000);
      }
      setTimeout(tick, 4000);
    })();
  }

  /* =======================================================================
   * Engagement pack 2 — added 21 Aug 2026 (Dev: dwell time + hand raises +
   * search/filters + hesitations + media depth + language). All listeners
   * are passive observers; nothing blocks or alters page behaviour.
   * ======================================================================= */

  // session-scoped dedupe flags (survive page navigations within the tab)
  function once(key) {
    try {
      var k = 'oasis_sig_' + key;
      if (sessionStorage.getItem(k)) return false;
      sessionStorage.setItem(k, '1');
      return true;
    } catch (e) {
      if (onceMem[key]) return false;
      onceMem[key] = 1;
      return true;
    }
  }
  var onceMem = {};

  // ----- 5. dwell time (active seconds on page) ---------------------------
  var dwell = { active: 0, lastInput: Date.now(), sent60: false, exitSent: false };
  function markInput() { dwell.lastInput = Date.now(); }
  ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'].forEach(function (ev) {
    window.addEventListener(ev, markInput, { passive: true, capture: true });
  });
  setInterval(function () {
    if (document.visibilityState !== 'visible') return;
    if (Date.now() - dwell.lastInput > 30000) return; // idle
    dwell.active += 5;
    if (!dwell.sent60 && dwell.active >= 60) {
      dwell.sent60 = true;
      track('Engaged On Page', {
        __unique_id: sessionId() + ':eng:' + location.pathname,
        seconds: 60,
        path: location.pathname.slice(0, 200),
        lot_handle: currentHandle() || undefined
      });
    }
  }, 5000);
  function sendDwellExit() {
    if (dwell.exitSent || dwell.active < 20) return;
    dwell.exitSent = true;
    // Warehouse-only by design (0-point, data), but it still goes through the
    // same stamping as everything else so it carries visit_number.
    warehouse('Page Dwell', {
      visit_number: visitNumber(),
      lot_handle: currentHandle() || undefined,
      path: location.pathname.slice(0, 200),
      active_seconds: dwell.active
    });
  }
  window.addEventListener('pagehide', sendDwellExit);
  window.addEventListener('beforeunload', sendDwellExit);

  // ----- 6. hand raises ---------------------------------------------------
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    var tel = e.target.closest('a[href^="tel:"]');
    if (tel && once('tel:' + tel.getAttribute('href'))) {
      track('Tapped Phone Number', {
        __unique_id: sessionId() + ':tel',
        number: (tel.getAttribute('href') || '').replace('tel:', '').slice(0, 24),
        path: location.pathname.slice(0, 200)
      });
    }
    var mail = e.target.closest('a[href^="mailto:"]');
    if (mail && once('mailto')) {
      track('Tapped Email Link', {
        __unique_id: sessionId() + ':mailto',
        path: location.pathname.slice(0, 200)
      });
    }
  }, true);

  var copies = 0;
  document.addEventListener('copy', function () {
    try {
      if (copies >= 2) return;
      var sel = String((window.getSelection && window.getSelection()) || '').replace(/\s+/g, ' ').trim();
      if (sel.length < 4) return;
      copies++;
      track('Copied Lot Details', {
        __unique_id: sessionId() + ':copy:' + copies,
        snippet: sel.slice(0, 90),
        path: location.pathname.slice(0, 200),
        lot_handle: currentHandle() || undefined
      });
    } catch (e) {}
  });

  function firePrint() {
    if (!once('print')) return;
    track('Printed Page', {
      __unique_id: sessionId() + ':print',
      path: location.pathname.slice(0, 200),
      lot_handle: currentHandle() || undefined
    });
  }
  window.addEventListener('beforeprint', firePrint);
  try {
    var pmq = window.matchMedia('print');
    if (pmq && pmq.addListener) pmq.addListener(function (m) { if (m.matches) firePrint(); });
  } catch (e) {}

  // ----- 7. what they hunt for: search + filters --------------------------
  function fireSearch(q, via) {
    q = String(q || '').replace(/\s+/g, ' ').trim().toLowerCase();
    if (!q || q.length < 2) return;
    if (!once('q:' + q.slice(0, 40))) return;
    track('Searched Site', {
      __unique_id: sessionId() + ':q:' + q.slice(0, 40),
      query: q.slice(0, 80),
      via: via
    });
  }
  document.addEventListener('submit', function (e) {
    var f = e.target;
    if (f && f.getAttribute && /\/search/.test(f.getAttribute('action') || '')) {
      var inp = f.querySelector('input[name="q"], input[type="search"]');
      if (inp) fireSearch(inp.value, 'form');
    }
  }, true);
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    var hit = e.target.closest('predictive-search a, [data-predictive-search] a');
    if (!hit) return;
    var box = document.querySelector('predictive-search input[type="search"], [data-predictive-search] input, input[name="q"]');
    if (box && box.value) fireSearch(box.value, 'suggestion');
  }, true);

  function checkFacets() {
    try {
      // 18 Sep 2026: both tests were anchored at the start of the path, so a
      // localised URL (/en-us/collections/all) matched neither and Filtered
      // Collection never fired on one. Two events in the metric's whole
      // history. Allow Shopify's optional market prefix.
      var onList = /^(\/[a-z]{2}(-[a-z]{2})?)?\/(collections\/|search)/i.test(location.pathname);
      if (!onList) return;
      var p = new URLSearchParams(location.search);
      var parts = [];
      var sort = p.get('sort_by');
      if (sort) parts.push('sort:' + sort);
      p.forEach(function (v, k) {
        if (k.indexOf('filter.') === 0 && v !== '') parts.push(k.replace('filter.', '') + ':' + String(v).slice(0, 30));
      });
      if (!parts.length) return;
      var sig = parts.join('|').slice(0, 120);
      if (!once('fac:' + sig)) return;
      track('Filtered Collection', {
        __unique_id: sessionId() + ':fac:' + sig.slice(0, 60),
        selection: sig,
        path: location.pathname.slice(0, 200),
        via: 'url-facets'
      });
    } catch (e) {}
  }
  try {
    var _ps = history.pushState, _rs = history.replaceState;
    history.pushState = function () { _ps.apply(this, arguments); setTimeout(checkFacets, 50); };
    history.replaceState = function () { _rs.apply(this, arguments); setTimeout(checkFacets, 50); };
  } catch (e) {}


  // ----- 7b. the lot filter at the top of the collection pages -------------
  // sections/lot-filter.liquid filters the grid in the browser. It never
  // touches the URL, so checkFacets() above, which reads query parameters,
  // could never see it: every island, land view and budget choice anyone has
  // ever made on a collection page went unrecorded. The section already
  // announces each change on a `lot-filter:change` event carrying the selected
  // values and how many lots survived, so listen to that rather than
  // re-implementing its logic, which would rot the moment the section changes.
  //
  // The section fires that event on load, on every click, and again each time
  // load-more appends cards. So: ignore the all-default resting state, ignore a
  // repeat of whatever was last sent, debounce a burst of clicks into the
  // selection they settled on, and stop after twelve.
  var lotFilter = { timer: null, last: '', fired: 0 };

  function collectionHandle() {
    var bare = (location.pathname || '').replace(/^\/[a-z]{2}(-[a-z]{2})?(?=\/)/i, '');
    if (bare.indexOf('/collections/') !== 0) return undefined;
    return bare.replace(/^\/collections\//, '').split('/')[0] || 'all';
  }

  // The event carries tag values ('littlecayman'); the buttons carry the label
  // a human would recognise ('Little Cayman'). Segments want the label.
  function lotFilterLabels(filters) {
    var out = {};
    Object.keys(filters || {}).forEach(function (group) {
      var value = filters[group];
      if (!value || value === 'all') return;
      var btn = null;
      try {
        btn = document.querySelector(
          '.lot-filter__btn[data-filter="' + group + '"][data-value="' + value + '"]'
        );
      } catch (e) {}
      out[group] = (btn && btn.getAttribute('data-label')) || value;
    });
    return out;
  }

  document.addEventListener('lot-filter:change', function (e) {
    try {
      var detail = (e && e.detail) || {};
      var filters = detail.filters || {};

      var chosen = Object.keys(filters).filter(function (g) {
        return filters[g] && filters[g] !== 'all';
      }).sort();

      // Everything set to All is the page's resting state, not a decision.
      if (!chosen.length) return;
      if (lotFilter.fired >= 12) return;

      var sig = chosen.map(function (g) { return g + ':' + filters[g]; }).join('|');
      if (sig === lotFilter.last) return;

      clearTimeout(lotFilter.timer);
      lotFilter.timer = setTimeout(function () {
        if (sig === lotFilter.last) return;
        lotFilter.last = sig;
        lotFilter.fired++;

        var labels = lotFilterLabels(filters);
        var results = typeof detail.visibleCount === 'number' ? detail.visibleCount : undefined;
        var shown = Object.keys(labels).map(function (g) { return labels[g]; }).join(', ');

        track('Filtered Collection', {
          __unique_id: sessionId() + ':lotfilter:' + sig.slice(0, 60),
          selection: sig.slice(0, 120),
          selection_label: shown.slice(0, 120) || undefined,
          island: labels.island,
          land_view: labels.view,
          budget: labels.budget,
          filters_applied: chosen.length,
          results: results,
          // Filtering your way to an empty grid is a different thing from
          // filtering your way to six lots, and worth segmenting on.
          no_results: results === 0 ? true : undefined,
          collection_handle: collectionHandle(),
          path: location.pathname.slice(0, 200),
          via: 'lot-filter'
        });
      }, 700);
    } catch (err) {}
  });

  // ----- 8. hesitations: dirty forms + chat opens -------------------------
  var dirtyForms = {}; // kind -> {dirty, submitted, firedLive}
  function formKind(f) {
    var hay = ((f.id || '') + ' ' + (f.className || '') + ' ' + ((f.closest('section,div[id]') || {}).id || '')).toLowerCase();
    if (/callback/.test(hay)) return 'callback';
    if (/blueprint/.test(hay)) return 'blueprint';
    if (/offer/.test(hay)) return 'offer';
    if (/member/.test(hay)) return 'member-club';
    if (/contact/.test(hay)) return 'contact';
    if (/newsletter|subscribe/.test(hay)) return 'newsletter';
    return 'other';
  }
  function watchableForm(f) {
    if (!f || !f.getAttribute) return false;
    var action = f.getAttribute('action') || '';
    if (/\/search|\/cart/.test(action)) return false;
    return !!f.querySelector('input[type="email"], input[type="tel"]');
  }
  document.addEventListener('input', function (e) {
    var f = e.target && e.target.form;
    if (!f || !watchableForm(f)) return;
    var kind = formKind(f);
    var st = dirtyForms[kind] || (dirtyForms[kind] = {});
    st.dirty = Date.now();
  }, true);
  document.addEventListener('submit', function (e) {
    var f = e.target;
    if (!f || !watchableForm(f)) return;
    var st = dirtyForms[formKind(f)] || (dirtyForms[formKind(f)] = {});
    st.submitted = true;
  }, true);
  function fireAbandon(kind, via) {
    var st = dirtyForms[kind];
    if (!st || !st.dirty || st.submitted || st.fired) return;
    if (!once('abandon:' + kind)) { st.fired = true; return; }
    st.fired = true;
    var payload = {
      __unique_id: sessionId() + ':abandon:' + kind,
      form_kind: kind,
      path: location.pathname.slice(0, 200)
    };
    if (via === 'exit') {
      warehouse('Abandoned Form', { form_kind: kind, path: payload.path });
    } else {
      track('Abandoned Form', payload);
    }
  }
  setInterval(function () {
    for (var kind in dirtyForms) {
      var st = dirtyForms[kind];
      if (st.dirty && !st.submitted && !st.fired && Date.now() - st.dirty > 75000) fireAbandon(kind, 'idle');
    }
  }, 15000);
  window.addEventListener('pagehide', function () {
    for (var kind in dirtyForms) fireAbandon(kind, 'exit');
  });

  document.addEventListener('click', function (e) {
    if (!e.target) return;
    // 27 Aug 2026: the live widget renders inside an OPEN shadow root on
    // <oasis-chat-widget>. By the time the click reaches document, the target
    // has been retargeted to the host element, so e.target.closest() sees
    // <oasis-chat-widget> and never the button. That is why Opened Chat Widget
    // never fired once despite 1,443 real conversations. composedPath() still
    // carries the real button, so walk that instead.
    var path = (typeof e.composedPath === 'function') ? e.composedPath() : [e.target];
    var opener = null;
    for (var ci = 0; ci < path.length; ci++) {
      var n = path[ci];
      if (!n || n.nodeType !== 1 || !n.classList) continue;
      // Closing the panel is not opening it.
      if (n.classList.contains('end-chat') || n.classList.contains('close')) return;
      if (n.classList.contains('nudge-dismiss')) return;
      var cls = (typeof n.className === 'string') ? n.className : '';
      if (n.classList.contains('launcher') || n.classList.contains('nudge-body') ||
          /oasis-chat|OasisChat/.test(cls) || /oasis-chat/i.test(n.id || '')) {
        opener = n;
        break;
      }
    }
    if (!opener) return;
    if (!once('chatopen')) return;
    track('Opened Chat Widget', {
      __unique_id: sessionId() + ':chatopen',
      via: opener.classList.contains('nudge-body') ? 'nudge' : 'launcher',
      path: location.pathname.slice(0, 200)
    });
  }, true);

  // ----- 9. media depth ---------------------------------------------------
  // The old watcher waited on #mediaModal, which does not exist on the lot
  // page template, so Zoomed Photo never fired. The real gallery has no
  // zoom or fullscreen control at all - it is prev/next plus thumbnails.
  // So the honest depth signal is how much of the gallery they actually got
  // through: someone who clicks to the 4th of 5 photos is genuinely looking.
  var galleryDepth = { seen: {}, count: 0, deepFired: false };
  function wireGalleryDepth() {
    var gal = document.querySelector('.gallery-carousel');
    if (!gal) return;
    var total = gal.querySelectorAll('[data-slide-index]').length ||
                gal.querySelectorAll('.oasis-gallery-thumb').length || 0;
    if (!total) return;

    function note(idx) {
      if (idx == null || galleryDepth.seen[idx]) return;
      galleryDepth.seen[idx] = 1;
      galleryDepth.count++;
      // Fire once when they have worked through most of the gallery.
      if (!galleryDepth.deepFired && total >= 3 && galleryDepth.count >= Math.max(3, Math.ceil(total * 0.6))) {
        galleryDepth.deepFired = true;
        track('Browsed Gallery', {
          __unique_id: sessionId() + ':gallery-deep:' + (currentHandle() || location.pathname),
          lot_handle: currentHandle() || undefined,
          slides_viewed: galleryDepth.count,
          slides_total: total,
          depth: 'most',
          path: location.pathname.slice(0, 200)
        });
      }
    }

    gal.addEventListener('click', function (e) {
      if (!e.target || !e.target.closest) return;
      var thumb = e.target.closest('.oasis-gallery-thumb');
      if (thumb) {
        var label = thumb.getAttribute('aria-label') || '';
        var m = label.match(/item\s+(\d+)/i);
        note(m ? m[1] : label.slice(0, 20));
        return;
      }
      if (e.target.closest('.carousel-btn')) {
        // prev/next: count the slide that is active just after the move
        setTimeout(function () {
          var active = gal.querySelector('.oasis-gallery-thumb.is-active');
          var al = active && active.getAttribute('aria-label');
          var mm = al && al.match(/item\s+(\d+)/i);
          note(mm ? mm[1] : 'step-' + galleryDepth.count);
        }, 120);
      }
    }, true);
  }

  var vidState = {}; // src -> {wasMuted, ended, acts:{}}
  function vidKey(v) { return (v.currentSrc || v.src || 'inline').split('/').pop().slice(0, 60); }
  function fireVideo(v, action) {
    var key = vidKey(v);
    var st = vidState[key] || (vidState[key] = { acts: {} });
    if (st.acts[action]) return;
    st.acts[action] = 1;
    track('Video Deep Engagement', {
      __unique_id: sessionId() + ':vde:' + action + ':' + key,
      action: action,
      video: key,
      lot_handle: currentHandle() || undefined
    });
  }
  document.addEventListener('volumechange', function (e) {
    var v = e.target;
    if (!v || v.tagName !== 'VIDEO') return;
    var st = vidState[vidKey(v)] || (vidState[vidKey(v)] = { acts: {} });
    if (st.wasMuted === undefined) { st.wasMuted = v.muted; return; }
    if (st.wasMuted && !v.muted && !v.paused) fireVideo(v, 'unmuted');
    st.wasMuted = v.muted;
  }, true);
  document.addEventListener('fullscreenchange', function () {
    var el = document.fullscreenElement;
    if (!el) return;
    var v = el.tagName === 'VIDEO' ? el : (el.querySelector && el.querySelector('video'));
    if (v) fireVideo(v, 'fullscreen');
  }, true);
  document.addEventListener('ended', function (e) {
    var v = e.target;
    if (!v || v.tagName !== 'VIDEO') return;
    (vidState[vidKey(v)] || (vidState[vidKey(v)] = { acts: {} })).ended = true;
  }, true);
  document.addEventListener('play', function (e) {
    var v = e.target;
    if (!v || v.tagName !== 'VIDEO') return;
    var st = vidState[vidKey(v)] || (vidState[vidKey(v)] = { acts: {} });
    if (st.ended) { st.ended = false; fireVideo(v, 'replayed'); }
  }, true);

  // ----- 10. language -----------------------------------------------------
  try {
    var navLang = (navigator.language || '').slice(0, 12);
    if (navLang && once('blang') && window.OasisTrack && window.OasisTrack.setProperty) {
      window.OasisTrack.setProperty('Browser Language', navLang);
    }
  } catch (e) {}
  function wireTranslate() {
    if (!('MutationObserver' in window)) return;
    var initialLang = (document.documentElement.getAttribute('lang') || 'en').slice(0, 12);
    new MutationObserver(function () {
      try {
        var html = document.documentElement;
        var lang = (html.getAttribute('lang') || '').slice(0, 12);
        var translated = /translated/.test(html.className || '') || (lang && lang !== initialLang);
        if (!translated) return;
        if (!once('translated')) return;
        track('Translated Page', {
          __unique_id: sessionId() + ':translated',
          to_lang: lang || 'unknown',
          path: location.pathname.slice(0, 200)
        });
      } catch (e) {}
    }).observe(document.documentElement, { attributes: true, attributeFilter: ['lang', 'class'] });
  }

  // ----- boot for pack 2 --------------------------------------------------
  /* =======================================================================
   * Engagement pack 3 — added 27 Aug 2026 (Dev picked: compared lots,
   * visit number, outbound clicks).
   * ======================================================================= */

  // ----- 10. Compared Lots -------------------------------------------------
  // Looking at two or more lots in one visit is a different behaviour from
  // looking at one: they are choosing, not browsing. The matrix already
  // treats it as a handover trigger, but it was only ever reconstructed in
  // the warehouse afterwards. This captures the moment, with the shortlist.
  function lotsSeen() {
    try { return JSON.parse(sessionStorage.getItem('oasis_lots_seen') || '[]'); } catch (e) { return []; }
  }
  function wireComparedLots() {
    var handle = currentHandle();
    if (!handle) return;
    var seen = lotsSeen();
    if (seen.some(function (x) { return x.handle === handle; })) return;

    var monthly = null;
    try { var m = readMonthly(); if (isFinite(m) && m > 0) monthly = Math.round(m); } catch (e) {}
    seen.push({ handle: handle, monthly: monthly });
    try { sessionStorage.setItem('oasis_lots_seen', JSON.stringify(seen.slice(0, 12))); } catch (e) {}

    var figures = seen.map(function (x) { return x.monthly; }).filter(function (x) { return x; });
    var payload = {
      lots: seen.map(function (x) { return x.handle; }).join(', ').slice(0, 200),
      lot_count: seen.length,
      monthly_low: figures.length ? Math.min.apply(null, figures) : undefined,
      monthly_high: figures.length ? Math.max.apply(null, figures) : undefined,
      path: location.pathname.slice(0, 200)
    };

    // Fire when the comparison starts, and again once the shortlist is long.
    if (seen.length === 2 && once('compared:2')) {
      payload.__unique_id = sessionId() + ':compared:2';
      track('Compared Lots', payload);
    } else if (seen.length === 4 && once('compared:4')) {
      payload.__unique_id = sessionId() + ':compared:4';
      payload.depth = 'shortlist';
      track('Compared Lots', payload);
    }
  }

  // ----- 11. outbound clicks ----------------------------------------------
  // Where their attention goes when it leaves us. A click to the YouTube
  // channel is interest; a click to a competitor is something else.
  var outboundFires = 0;
  function outboundKind(host) {
    if (/youtube\.com$|youtu\.be$/.test(host)) return 'youtube';
    if (/facebook\.com$|fb\.com$|messenger\.com$/.test(host)) return 'facebook';
    if (/instagram\.com$/.test(host)) return 'instagram';
    if (/tiktok\.com$/.test(host)) return 'tiktok';
    if (/linkedin\.com$/.test(host)) return 'linkedin';
    if (/(^|\.)x\.com$|twitter\.com$/.test(host)) return 'x';
    if (/wa\.me$|whatsapp\.com$/.test(host)) return 'whatsapp';
    if (/calendly\.com$/.test(host)) return 'calendly';
    if (/betterproposals\.io$/.test(host)) return 'proposal';
    if (/fygaro\.com$/.test(host)) return 'payment';
    return 'other';
  }
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    var a = e.target.closest('a[href]');
    if (!a) return;
    var raw = a.getAttribute('href') || '';
    if (/^(#|mailto:|tel:|javascript:)/i.test(raw)) return;

    var u;
    try { u = new URL(a.href, location.href); } catch (err) { return; }
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return;

    var host = u.hostname.replace(/^www\./, '');
    var self = location.hostname.replace(/^www\./, '');
    if (!host || host === self) return;
    // Shopify's own infrastructure is not "outbound" in any useful sense.
    if (/(^|\.)shopify\.com$|(^|\.)shopifycdn\.com$|(^|\.)myshopify\.com$/.test(host)) return;

    if (outboundFires >= 4) return;
    outboundFires++;
    var kind = outboundKind(host);
    track('Clicked Outbound Link', {
      __unique_id: sessionId() + ':out:' + kind + ':' + outboundFires,
      destination: kind,
      destination_host: host.slice(0, 80),
      link_text: (a.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 60) || undefined,
      path: location.pathname.slice(0, 200)
    });
  }, true);

  function bootPack2() {
    try { wireComparedLots(); } catch (e) {}
    try { wireGalleryDepth(); } catch (e) {}
    wireTranslate();
    checkFacets();
    if (/^(\/[a-z]{2}(-[a-z]{2})?)?\/search/i.test(location.pathname)) {
      try { fireSearch(new URLSearchParams(location.search).get('q'), 'results-page'); } catch (e) {}
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootPack2);
  } else {
    bootPack2();
  }


  /* =======================================================================
   * Engagement pack 4 — lot page detail, added 27 Aug 2026.
   *
   * Dev's ask: when a lead has been on the same lot page ten times, a rep
   * should be able to see what they actually DID on it, not just that they
   * were there. So: how far down they read (by the page's own section names,
   * not a percentage), whether they opened the concept plans, whether they
   * played any of the owner review videos, whether they touched the map,
   * and which questions they opened.
   *
   * All of it is passive. Nothing here blocks or alters the page.
   * ======================================================================= */

  // The lot page's landmark sections, top to bottom. Ids carry the product
  // id as a suffix (sec-payment-10361644122378), so match on the prefix.
  var LOT_SECTIONS = [
    { key: 'payment',  prefix: 'sec-payment',       label: 'the payment plan' },
    { key: 'specs',    prefix: 'sec-specs',         label: 'the plot specification' },
    { key: 'desc',     prefix: 'sec-desc',          label: 'the description' },
    { key: 'included', prefix: 'sec-included',      label: "what's included" },
    { key: 'location', prefix: 'sec-location',      label: 'the location and map' },
    { key: 'reviews',  prefix: 'sec-owner-stories', label: 'the owner reviews' },
    { key: 'concepts', prefix: 'sec-concepts',      label: 'the concept plans' },
    { key: 'faqs',     prefix: null,                label: 'the questions at the bottom' }
  ];

  function isLotPage() {
    var ctx = window.OasisTrackContext || {};
    return ctx.pageType === 'product' && !!(ctx.product && ctx.product.handle);
  }

  function wireLotSections() {
    if (!isLotPage() || !('IntersectionObserver' in window)) return;
    var handle = currentHandle();
    var found = [];

    for (var i = 0; i < LOT_SECTIONS.length; i++) {
      var def = LOT_SECTIONS[i], el = null;
      if (def.prefix) {
        el = document.querySelector('[id^="' + def.prefix + '"]');
      } else if (def.key === 'faqs') {
        var firstFaq = document.querySelector('details.faq-details');
        el = firstFaq ? (firstFaq.parentElement || firstFaq) : null;
      }
      if (el) found.push({ key: def.key, label: def.label, el: el });
    }
    if (!found.length) return;

    // Order by real position, so a theme reshuffle cannot make "deepest"
    // mean the wrong thing.
    found.sort(function (a, b) {
      return a.el.getBoundingClientRect().top - b.el.getBoundingClientRect().top;
    });

    var total = found.length, fired = 0;
    var io = new IntersectionObserver(function (entries) {
      for (var j = 0; j < entries.length; j++) {
        var entry = entries[j];
        if (!entry.isIntersecting) continue;
        var hit = null;
        for (var k = 0; k < found.length; k++) {
          if (found[k].el === entry.target) { hit = found[k]; break; }
        }
        if (!hit) continue;
        io.unobserve(entry.target);
        if (fired >= 8) continue;
        if (!once('sec:' + handle + ':' + hit.key)) continue;
        fired++;
        var idx = found.indexOf(hit) + 1;
        track('Reached Section', {
          __unique_id: sessionId() + ':sec:' + handle + ':' + hit.key,
          section: hit.key,
          section_label: hit.label,
          section_index: idx,
          sections_total: total,
          lot_handle: handle,
          path: location.pathname.slice(0, 200)
        });
      }
    }, { threshold: 0.25 });

    for (var m = 0; m < found.length; m++) io.observe(found[m].el);
  }

  // ----- concept plans ----------------------------------------------------
  // <details class="plans-disclosure"> — opening it is a build-intent signal:
  // they are picturing a house on the plot, not just pricing land.
  function wireConceptPlans() {
    var d = document.querySelector('details.plans-disclosure');
    if (!d) return;
    var handle = currentHandle();
    d.addEventListener('toggle', function () {
      if (!d.open) return;
      if (!once('concepts:' + handle)) return;
      track('Opened Concept Plans', {
        __unique_id: sessionId() + ':concepts:' + (handle || location.pathname),
        lot_handle: handle || undefined,
        path: location.pathname.slice(0, 200)
      });
    });
  }

  // ----- owner review videos ----------------------------------------------
  // Each review opens a modal; the button carries the YouTube id and the
  // reviewer's story in its aria-label.
  var reviewPlays = 0;
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    var b = e.target.closest('[data-owner-play], button.owner-stories__media');
    if (!b) return;
    if (reviewPlays >= 3) return;
    var vid = b.getAttribute('data-video-id') || '';
    var handle = currentHandle();
    if (!once('review:' + handle + ':' + (vid || reviewPlays))) return;
    reviewPlays++;
    var titleAttr = (b.getAttribute('aria-label') || '').replace(/^play video:\s*/i, '').trim();
    track('Played Review Video', {
      __unique_id: sessionId() + ':review:' + (vid || reviewPlays),
      video_id: vid || undefined,
      video_title: titleAttr ? titleAttr.slice(0, 120) : undefined,
      lot_handle: handle || undefined,
      path: location.pathname.slice(0, 200)
    });
  }, true);

  // ----- the map ----------------------------------------------------------
  // The map is an <object> embed, so clicks inside it never reach this
  // document. Two honest tells instead: a pointer press on the container,
  // and the window losing focus while the pointer sits over the map, which
  // is what happens the moment someone drags or zooms inside the embed.
  function wireMap() {
    var box = document.querySelector('.map-container');
    if (!box) return;
    var handle = currentHandle();
    var overMap = false;

    function fireMap(how) {
      if (!once('map:' + handle)) return;
      track('Used Map', {
        __unique_id: sessionId() + ':map:' + (handle || location.pathname),
        via: how,
        lot_handle: handle || undefined,
        path: location.pathname.slice(0, 200)
      });
    }

    box.addEventListener('mouseenter', function () { overMap = true; });
    box.addEventListener('mouseleave', function () { overMap = false; });
    box.addEventListener('pointerdown', function () { fireMap('pressed'); }, true);
    window.addEventListener('blur', function () {
      if (overMap) fireMap('embed');
    });
    var fs = box.querySelector('a.map-fullscreen-btn');
    if (fs) fs.addEventListener('click', function () { fireMap('fullscreen'); }, true);
  }

  function bootPack4() {
    try { wireLotSections(); } catch (e) {}
    try { wireConceptPlans(); } catch (e) {}
    try { wireMap(); } catch (e) {}
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootPack4);
  } else {
    bootPack4();
  }

  window.OasisIdentityTrack = { __loaded: true, track: track, warehouse: warehouse, ping: ping };
})();
